import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { isEqual } from 'lodash';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';

import {
    createPartnerSearchPlaceholderData,
    resolvePartnerCompanySourceId,
} from 'components/fbm/utils/partnerCompanySourceUtils';
import { UserContext } from 'contexts/UserContext';
import { fromUrl, pushToUrl, removeEmptyFilters, toApi } from 'shared';

export const DEFAULT_PAGE_SIZE = 50;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function buildFiltersFromSearch(searchString, transformConfig) {
    const parsed = fromUrl(searchString, transformConfig);
    return {
        ...parsed,
        pageSize: parsed.pageSize ? Number(parsed.pageSize) : DEFAULT_PAGE_SIZE,
    };
}

function createFreshAccountFilters() {
    return { pageSize: DEFAULT_PAGE_SIZE };
}

const defaultEnrichApiFilters = (transformed) => transformed;

/**
 * Shared partner purchase-order list: URL filters, cursor pagination, account reset, modal open state.
 *
 * @param {Object} props
 * @param {Object} props.transformConfig
 * @param {Object} props.sortConfig
 * @param {(filters: Object, options: Object) => Object} props.useSearch
 * @param {(transformed: Object, ctx: { companySourceId: number|null }) => Object} [props.enrichApiFilters]
 * @param {React.ComponentType} props.Filters
 * @param {React.ComponentType} props.Table
 * @param {React.ComponentType} props.LineItemsModal
 * @param {string} props.errorPrefix
 * @param {{ none: string, noAccount: string, unmapped: string }} props.emptyMessages
 */
export function PartnerPurchaseOrderListShell({
    emptyMessages,
    enrichApiFilters = defaultEnrichApiFilters,
    errorPrefix,
    Filters,
    LineItemsModal,
    sortConfig,
    Table,
    transformConfig,
    useSearch,
}) {
    const location = useLocation();
    const { fbmAccount } = useContext(UserContext);

    const fbmAccountId = fbmAccount?.id;
    const companySourceId = useMemo(() => resolvePartnerCompanySourceId(fbmAccount), [fbmAccount]);

    const [filters, setFilters] = useState(() =>
        buildFiltersFromSearch(
            typeof window !== 'undefined' ? window.location.search : location.search,
            transformConfig,
        ),
    );
    const [currentCursor, setCurrentCursor] = useState(undefined);
    const [cursorHistory, setCursorHistory] = useState([]);
    const storedTotalCountRef = useRef(undefined);
    const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState(null);
    const previousFbmAccountIdRef = useRef(fbmAccountId);

    const pageSize = filters.pageSize || DEFAULT_PAGE_SIZE;

    const apiFilters = useMemo(() => {
        const transformed = toApi(filters, transformConfig);
        if (!transformed.pageSize) {
            transformed.pageSize = DEFAULT_PAGE_SIZE;
        }
        if (companySourceId != null) {
            transformed.companySourceIds = [companySourceId];
        }
        return enrichApiFilters(transformed, { companySourceId });
    }, [companySourceId, enrichApiFilters, filters, transformConfig]);

    const paginatedFilters = useMemo(
        () => ({
            ...apiFilters,
            cursor: currentCursor,
            ...(currentCursor !== undefined &&
                storedTotalCountRef.current !== undefined && {
                    totalCount: storedTotalCountRef.current,
                }),
        }),
        [apiFilters, currentCursor],
    );

    const canSearch = Boolean(fbmAccountId && companySourceId != null);

    const placeholderData = useMemo(
        () => createPartnerSearchPlaceholderData(companySourceId != null ? [companySourceId] : undefined),
        [companySourceId],
    );

    const paginatedQuery = useSearch(paginatedFilters, {
        enabled: canSearch,
        placeholderData,
        retry: false,
    });

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        const liveSearch =
            (window.location.search ? window.location.search : '') ||
            (typeof location.search === 'string' ? location.search : '');
        const nextState = buildFiltersFromSearch(liveSearch, transformConfig);

        setFilters((prev) => {
            if (isEqual(prev, nextState)) {
                return prev;
            }
            return nextState;
        });
        setCursorHistory([]);
        setCurrentCursor(undefined);
        storedTotalCountRef.current = undefined;
    }, [location.search, transformConfig]);

    useEffect(() => {
        const previousId = previousFbmAccountIdRef.current;
        previousFbmAccountIdRef.current = fbmAccountId;
        if (previousId === fbmAccountId) {
            return;
        }
        const freshFilters = createFreshAccountFilters();
        setCursorHistory([]);
        setCurrentCursor(undefined);
        storedTotalCountRef.current = undefined;
        setSelectedPurchaseOrder(null);
        setFilters(freshFilters);
        pushToUrl(removeEmptyFilters(freshFilters), transformConfig, { replace: true });
    }, [fbmAccountId, transformConfig]);

    useEffect(() => {
        if (paginatedQuery.data?.pagination?.totalCount !== undefined) {
            storedTotalCountRef.current = paginatedQuery.data.pagination.totalCount;
        }
    }, [paginatedQuery.data?.pagination?.totalCount]);

    const { data, error, isFetching, isLoading } = paginatedQuery;

    const rows = useMemo(() => data?.rows ?? [], [data]);
    const pagination = useMemo(() => data?.pagination ?? {}, [data]);
    const totalCount = pagination.totalCount ?? storedTotalCountRef.current ?? 0;
    const showInitialLoading = Boolean(canSearch && isLoading && rows.length === 0);

    const handleFilterChange = useCallback(
        (nextFilters) => {
            const updatedState = {
                ...nextFilters,
                pageSize: filters.pageSize || DEFAULT_PAGE_SIZE,
                ...(filters.sortField && { sortField: filters.sortField }),
                ...(filters.sortDir && { sortDir: filters.sortDir }),
            };

            setCursorHistory([]);
            setCurrentCursor(undefined);
            storedTotalCountRef.current = undefined;
            setFilters(updatedState);
            pushToUrl(removeEmptyFilters(updatedState), transformConfig, { replace: false });
        },
        [filters.pageSize, filters.sortField, filters.sortDir, transformConfig],
    );

    const handleNextPage = useCallback(() => {
        if (!pagination.hasMore || pagination.nextCursor == null || pagination.nextCursor === '') {
            return;
        }
        const rawCursor = pagination.nextCursor;
        let nextCursor = rawCursor;
        if (typeof rawCursor === 'object') {
            nextCursor = JSON.stringify(rawCursor);
        }
        setCursorHistory((prev) => [...prev, currentCursor]);
        setCurrentCursor(nextCursor);
    }, [pagination, currentCursor]);

    const handlePreviousPage = useCallback(() => {
        if (cursorHistory.length > 0) {
            const newHistory = [...cursorHistory];
            const previousCursor = newHistory.pop();
            setCursorHistory(newHistory);
            setCurrentCursor(previousCursor);
        }
    }, [cursorHistory]);

    const handlePageSizeChange = useCallback(
        (newPageSize) => {
            const nextState = { ...filters, pageSize: newPageSize };
            setCursorHistory([]);
            setCurrentCursor(undefined);
            storedTotalCountRef.current = undefined;
            setFilters(nextState);
            pushToUrl(removeEmptyFilters(nextState), transformConfig, { replace: true });
        },
        [filters, transformConfig],
    );

    const sorting = useMemo(() => {
        const apiField = filters.sortField;
        if (!apiField) return [];
        const columnId = Object.entries(sortConfig.sortableFields).find(([, cfg]) => cfg.apiField === apiField)?.[0];
        if (!columnId) return [];
        return [{ id: columnId, desc: filters.sortDir === 'desc' }];
    }, [filters.sortField, filters.sortDir, sortConfig]);

    const handleSortingChange = useCallback(
        (updater) => {
            const nextSorting = typeof updater === 'function' ? updater(sorting) : updater;
            const sortEntry = nextSorting?.[0];
            const sortField = sortEntry ? sortConfig.sortableFields[sortEntry.id]?.apiField : undefined;
            let sortDir;
            if (sortEntry == null) {
                sortDir = undefined;
            } else {
                sortDir = sortEntry.desc ? 'desc' : 'asc';
            }

            const { sortDir: _sd, sortField: _sf, ...restFilters } = filters;
            const nextState = sortField ? { ...filters, sortField, sortDir } : restFilters;
            setCursorHistory([]);
            setCurrentCursor(undefined);
            storedTotalCountRef.current = undefined;
            setFilters(nextState);
            pushToUrl(removeEmptyFilters(nextState), transformConfig, { replace: true });
        },
        [filters, sorting, sortConfig, transformConfig],
    );

    const handleViewPurchaseOrder = useCallback((purchaseOrder) => {
        setSelectedPurchaseOrder(purchaseOrder ?? null);
    }, []);

    const handleModalOpenChange = useCallback((open) => {
        if (!open) {
            setSelectedPurchaseOrder(null);
        }
    }, []);

    useEffect(() => {
        if (error) {
            const messageParts = [errorPrefix, error.message];
            toast.error(messageParts.filter(Boolean).join(' '));
        }
    }, [error, errorPrefix]);

    const currentPage = cursorHistory.length + 1;
    const totalPages = totalCount > 0 ? Math.ceil(totalCount / pageSize) : undefined;

    const filterValuesForUI = useMemo(() => {
        const { pageSize: _ps, sortDir: _sd, sortField: _sf, ...filterOnly } = filters;
        return filterOnly;
    }, [filters]);

    let emptyMessage = emptyMessages.none;
    if (!fbmAccountId) {
        emptyMessage = emptyMessages.noAccount;
    } else if (companySourceId == null) {
        emptyMessage = emptyMessages.unmapped;
    }

    return (
        <>
            {canSearch && (
                <div className="mt-4 px-4">
                    <Filters
                        initialValues={filterValuesForUI}
                        onChange={handleFilterChange}
                        whitelistedFacilityIds={fbmAccount?.facilityIDs}
                    />
                </div>
            )}
            <div className="flex flex-1 flex-col">
                {error ? (
                    <div className="mx-4 mb-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                        {[errorPrefix, error.message].filter(Boolean).join(' ')}
                    </div>
                ) : null}
                <Table
                    data={canSearch ? rows : []}
                    totalCount={canSearch ? totalCount : 0}
                    isLoading={showInitialLoading}
                    isFetching={canSearch && isFetching}
                    hasNextPage={Boolean(pagination.hasMore)}
                    hasPrevious={cursorHistory.length > 0}
                    onPrevious={handlePreviousPage}
                    onNext={handleNextPage}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    pageSize={pageSize}
                    onPageSizeChange={handlePageSizeChange}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    sorting={sorting}
                    onSortingChange={handleSortingChange}
                    onViewPurchaseOrder={handleViewPurchaseOrder}
                    emptyMessage={emptyMessage}
                />
            </div>
            <LineItemsModal
                purchaseOrder={selectedPurchaseOrder}
                open={selectedPurchaseOrder != null}
                onOpenChange={handleModalOpenChange}
            />
        </>
    );
}
